/**
 * Themed word banks for word search (and themed bingo).
 *
 * Each bank under `./banks/` is produced by `tools/content-pipeline`'s LLM
 * generate → filter → LLM-as-judge flow against the manifest
 * `tools/content-pipeline/manifests/wordsearch-themedshape-v1.yaml`, and holds
 * ~50–60 senior-audience-vetted words (4–9 letters, A–Z only, era-appropriate
 * for residents 70–95+). Refresh a bank by re-running the pipeline, not by
 * hand-editing the file — the header in each records generator, judge, and
 * acceptance threshold.
 *
 * The pipeline emits a `{ word }` envelope per entry. {@link WORD_BANKS} is the
 * flat, de-duplicated form every consumer actually wants; the raw arrays stay
 * exported for tooling that needs the envelope.
 */
export { animalsWords } from './banks/animals.js';
export { filmsWords } from './banks/films.js';
export { gardenWords } from './banks/garden.js';
export { kitchenWords } from './banks/kitchen.js';
export { musicWords } from './banks/music.js';
export { travelWords } from './banks/travel.js';
import { animalsWords } from './banks/animals.js';
import { filmsWords } from './banks/films.js';
import { gardenWords } from './banks/garden.js';
import { kitchenWords } from './banks/kitchen.js';
import { musicWords } from './banks/music.js';
import { travelWords } from './banks/travel.js';
export const WORD_BANK_SLUGS = [
    'garden',
    'music',
    'kitchen',
    'films',
    'travel',
    'animals',
];
/**
 * Strip the `{ word }` envelope and drop duplicates, preserving order.
 *
 * The generation schema forbids duplicates per bank, but has slipped one
 * through before (`music` once carried OPERA twice), and a duplicate word is
 * more than cosmetic downstream: the placement engine would hide it twice and
 * a printed word list would ask residents to find it twice.
 */
function flatten(entries) {
    const seen = new Set();
    const out = [];
    for (const { word } of entries) {
        if (!seen.has(word)) {
            seen.add(word);
            out.push(word);
        }
    }
    return out;
}
/** The flat, de-duplicated word pool for every theme. */
export const WORD_BANKS = {
    garden: flatten(gardenWords),
    music: flatten(musicWords),
    kitchen: flatten(kitchenWords),
    films: flatten(filmsWords),
    travel: flatten(travelWords),
    animals: flatten(animalsWords),
};
export function wordBank(slug) {
    const pool = WORD_BANKS[slug];
    if (!pool)
        throw new Error(`Unknown word bank: ${slug}`);
    return pool;
}
/** Live pool sizes per theme — for census counts, generated not transcribed. */
export function wordBankCounts() {
    const counts = {
        garden: WORD_BANKS.garden.length,
        music: WORD_BANKS.music.length,
        kitchen: WORD_BANKS.kitchen.length,
        films: WORD_BANKS.films.length,
        travel: WORD_BANKS.travel.length,
        animals: WORD_BANKS.animals.length,
    };
    return {
        ...counts,
        total: WORD_BANK_SLUGS.reduce((n, slug) => n + counts[slug], 0),
    };
}
//# sourceMappingURL=index.js.map